import { BookabilityAttributes } from "@src/appV2/OpenShifts/ShiftAction";
import { SHIFT_BOOKED_CONTEXT } from "@src/constants";
import { SEARCH_MODE } from "@src/constants";
import { Shift } from "@src/lib/interface";

import { UpdateShiftListFn } from "./custom-hooks/interfaces";
import { HcfProfileSource } from "./facilityDetails/model";
import { IBookedBy, IShiftSlot } from "../components/workWithFriends/interface";

enum ShiftItemCategory {
  MISSING_CBH_DOCUMENTS = "MISSING_CBH_DOCUMENTS",
  BOOKING_WARNING = "BOOKING_WARNING",
  WAITING_FOR_APPROVE = "WAITING_FOR_APPROVE",
  NOT_READY_TO_BOOK_SHIFT = "NOT_READY_TO_BOOK_SHIFT",
  /**
   * We need to inform to HCP when they're booking at instantpay shift
   */
  WELCOME_TO_INSTANTPAY = "WELCOME_TO_INSTANTPAY",
  NO_LOCATION_PERMISSION = "NO_LOCATION_PERMISSION",
  // documents are expiring before end of a shift
  MISSING_CBH_DOCUMENTS_BEFORE_A_SHIFT_END = "MISSING_CBH_DOCUMENTS_BEFORE_A_SHIFT_END",
  OVERWORK_BOOKING = "OVERWORK_BOOKING",
  // these are used with the experimental - now dead - onboarding workflows but are referenced
  STRIPE_NOT_CONNECTED_AND_MISSING_HCP_DOCUMENTS = "STRIPE_NOT_CONNECTED_AND_MISSING_HCP_DOCUMENTS",
  STRIPE_NOT_CONNECTED_AND_MISSING_CBH_DOCUMENTS_BEFORE_A_SHIFT_END = "STRIPE_NOT_CONNECTED_AND_MISSING_CBH_DOCUMENTS_BEFORE_A_SHIFT_END",
  SHIFT_PAY_CHANGED = "SHIFT_PAY_CHANGED",
}

enum ShiftItemActionType {
  /**
   * Based on all the missing document category is
   * set to show action button and alerts.
   */
  SET_CATEGORY = "SET_CATEGORY",
  /**
   * When an API call to book a shift is in progress.
   */
  SUBMITTING = "SUBMITTING",
  /**
   * when API request is completed and booking a shift is complete.
   * This state help in triggering logEvent when an action is done,
   * in it's respective components outside the custom-hook.
   */
  SUBMITTED = "SUBMITTED",
  /**
   * Flag used to reset state, when an error is occurred.
   */
  ERROR_SUBMITTING = "ERROR_SUBMITTING",
  /**
   * Shows alert message based on CTA
   * Ex: See What's Missing or Upload a COVID Test
   */
  OPEN_ALERT = "OPEN_ALERT",
  /**
   * Closes the alert that is opened by `OPEN_ALERT`
   */
  CLOSE_ALERT = "CLOSE_ALERT",
  CONFIGURE_PUSH_NOTIFICATION = "CONFIGURE_PUSH_NOTIFICATION",
  /**
   * This action doesn't guarantee opening of push notification,
   * it opens based on push notification configurations.
   */
  OPEN_PUSH_NOTIFICATION = "OPEN_PISH_NOTIFICATION",
  CLOSE_PUSH_NOTIFICATION = "CLOSE_PUSH_NOTIFICATION",
  WORK_APPROVED = "WORK_APPROVED",
  SHOWED_INTEREST = "SHOWED_INTEREST",
  SET_MISSING_DOCS = "SET_MISSING_DOCS",
  SET_CHECKING_DOCS = "SET_CHECKING_DOCS",
  OPEN_LOCATION_PRIMER_PAGE = "OPEN_LOCATION_PRIMER_PAGE",
  CLOSE_LOCATION_PRIMER_PAGE = "CLOSE_LOCATION_PRIMER_PAGE",
  SET_SHIFT_PAY_CHANGED = "SET_SHIFT_PAY_CHANGED",
  REMOVE_MARK_INTEREST_FLAG = "REMOVE_MARK_INTEREST_FLAG",
}

interface ShiftDateMap {
  [date: string]: Shift[];
}

interface GroupItemDescriptionProps {
  shiftName: string;
  openShiftLength: number;
  myShiftLength: number;
}
interface GroupItemProps {
  shiftName: string;
  myShiftMap: ShiftDateMap;
  openShiftMap: ShiftDateMap;
  onClick: (shiftName: string) => void;
  friendsList?: IBookedBy[];
}

interface RequestRatingsOptions {
  reviewFor: "FACILITY" | "AGENT";
  facilityId?: string;
  agentId?: string;
}

interface RequestDetailedRatings {
  filter: RequestRatingsOptions;
  page: number;
}

interface ResponseDetailedRatings {
  reasons: string[];
  createdAt: string;
  otherReason: string;
  rating: number;
  review: string;
  agent: {
    userId: string;
    name: string;
  };
}

interface ShiftItemState extends Pick<ShiftItemProps, "pushNotification"> {
  isLoading: boolean;
  category: ShiftItemCategory;
  alertOpen: boolean;
  /**
   * set true while API call is active during booking
   * of a shift by HCP.
   */
  submitting: boolean;
  /**
   * set true when API call is completed.
   */
  submitted: boolean;
  // Decided based on properties inside `pushNotification`
  pushNotificationOpen: boolean;
  workApproved: boolean;
  interested: boolean;
  isMarkInterest?: boolean;
}

interface UseShiftItemHookReturnType extends ShiftItemState {
  /**
   * on Calling opens the alert dialog.
   */
  loadAlertDialog: (callback?: () => void) => void;
  /**
   * on clicking on proceed/next action the alert dialog box
   */
  onCancel: () => void;
  onConfirm: () => void;
  interested: boolean;
  closePushNotification: () => void;
  setClickedInterestInShift?: () => void;
  workApproved: boolean;
  isLastMinuteShift?: boolean;
  isUrgentShift?: boolean;
  missingDocs: Set<any>;
  checkingDocs: boolean;
  sourceContext?: (typeof SHIFT_BOOKED_CONTEXT)[keyof typeof SHIFT_BOOKED_CONTEXT];
  onClosePrimerPage: () => void;
  changedShiftPayText: string;
}

interface ShiftItemViewProps
  extends Pick<
      UseShiftItemHookReturnType,
      | "category"
      | "submitting"
      | "submitted"
      | "isLoading"
      | "loadAlertDialog"
      | "workApproved"
      | "interested"
      | "isLastMinuteShift"
    >,
    Pick<ShiftItemProps, "isInstantBookingShift" | "showFacilityDetails" | "onItemClick"> {
  shiftDetails: Shift;
  onClickOnFacilityDetails: (displayExtraTimePayCard?: boolean) => void;
  isInstantPayAllowed: boolean;
  isUrgentShift?: boolean;
  checkingDocs?: boolean;
  priorityShift: boolean;
  setPriorityShift: Function;
  shiftSlot?: IShiftSlot;
  isRateNegotiationActive?: boolean;
  searchMode?: SEARCH_MODE;
  updateShiftFields?: (shift: Shift, fields: string[]) => void;
  allowedAgentReq?: string[];
  isSingleSourceOfBookabilityEnabled?: boolean;
  shiftBookability?: BookabilityAttributes;
  updateShiftList?: UpdateShiftListFn;
}

interface ShiftItemProps {
  isAnimated?: boolean;
  shiftName: string;
  shift: Shift;
  showFacilityDetails?: boolean;
  /**
   * Flag to determine if the shift supports instant booking?
   */
  isInstantBookingShift?: boolean;
  isInterested?: boolean;
  shiftActivityLog?: Function;
  updateShiftList?: UpdateShiftListFn;
  isHcpInstantPayEnabled?: boolean;
  onFacilityDetailsClick?: (
    shift: Shift,
    isInstantBook: boolean,
    displayExtraTimePayCard?: boolean
  ) => void;

  goToDocumentsPage?: (shift: Shift) => void;
  onShiftClaim?: (shiftId: string, isAlreadyGrabbed?: boolean, startAt?: string) => void;
  className?: string;
  shiftPay?: number;
  setClickedInterestInShift?: (value: boolean) => void;
  isHCFInstantPayProhibited?: boolean;
  isDisabled?: boolean;
  onMarkedInterest?: (shiftId?: string) => void;
  handleShiftActivityLog?: (shiftId?: string, type?: string) => void;
  isLastMinuteShift?: boolean;
  /**
   * Finds any conflicting shifts with the current booking
   * shift.
   */
  validator?: (shift: Shift) => boolean;
  pushNotification?: {
    accepted?: boolean;
    pushFlag?: boolean;
    isPushSet?: boolean;
    isNotificationAlertShown?: boolean;
  };
  /**
   * CH-4051 logger
   */
  loggerSearchMode?: string;
  onItemClick?: () => void;
  isUrgentShift?: boolean;
  searchMode?: SEARCH_MODE;
  availableShiftIds?: string[] | undefined;
  filterDistance?: number;
  filterDays?: number;
  hcfProfileSource?: HcfProfileSource;
  shiftSlots?: IShiftSlot[];
  updateShiftFields?: (shift: Shift, fields: string[]) => void;
  allowedAgentReq?: string[];
  isSingleSourceOfBookabilityEnabled?: boolean;
  shiftBookability?: BookabilityAttributes;
}

interface ShiftItemAlertsProps
  extends Pick<ShiftItemProps, "shift" | "isInstantBookingShift">,
    Pick<
      UseShiftItemHookReturnType,
      | "category"
      | "alertOpen"
      | "onCancel"
      | "onConfirm"
      | "pushNotificationOpen"
      | "closePushNotification"
      | "isUrgentShift"
      | "missingDocs"
      | "onClosePrimerPage"
      | "changedShiftPayText"
    > {}

interface ToolTipProps {
  toolTipHandler?: Function;
  content: any;
  buttonName?: string;
  toolTipClass?: string;
}

interface ShiftListGroupState {
  /**
   * open shifts.
   */
  open: Shift[];
  /**
   * shifts owned by the user.
   */
  assigned: Shift[];
}
interface ShiftListProps {
  day: string;
  shiftName: string;
  shifts: ShiftListGroupState;
  onClose: () => void;
  goToDocumentsPage?: () => void;
  getOpenAndMyShifts: () => void;
  loadingShiftList?: boolean;
  searchMode: SEARCH_MODE;
  distancePreference: number;
  qualificationPreference: string;
  filterDays: number;
  shiftSlots?: IShiftSlot[];
  updateShiftFields?: (shift: Shift, fields: string[]) => void;
}

interface ShiftNameLoaderProps {
  listLength: number;
}

interface ShiftInlineAlertsProps {
  shift: Shift;
  /**
   * determines if agent showed interest in present shift.
   */
  interested: boolean;
  category: ShiftItemCategory;
}

export interface CbhDocument {
  reqId: string;
  name: string;
  visibleToHCP: boolean;
}
interface MissingDocumentsItemProps {
  isOpen: boolean;
  /**
   * Function to call when the alert is closed to update the state.
   */
  onDidDismiss: () => void;
  onConfirm: () => void;
  shift?: Shift;
  category: ShiftItemCategory;
  missingDocuments: Set<CbhDocument>;
  expiringInFuture?: boolean;
}

interface IShiftPayChangedModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  changedShiftPayText: string;
  onConfirm: () => void;
}

export {
  MissingDocumentsItemProps,
  ShiftDateMap,
  GroupItemDescriptionProps,
  GroupItemProps,
  ShiftItemProps,
  ShiftListProps,
  ShiftNameLoaderProps,
  ToolTipProps,
  ShiftItemCategory,
  ShiftInlineAlertsProps,
  ShiftItemActionType,
  ShiftItemState,
  UseShiftItemHookReturnType,
  ShiftItemViewProps,
  ShiftItemAlertsProps,
  ShiftListGroupState,
  RequestDetailedRatings,
  ResponseDetailedRatings,
  IShiftPayChangedModalProps,
};
